import React from 'react';
import { Link } from 'react-router-dom';
import { FaFacebook, FaInstagram, FaTiktok, FaTwitter, FaYoutube, FaUsers, FaHeart, FaEye, FaShare, FaComment, FaPlay } from 'react-icons/fa';

const Services = () => {
  const services = [
    {
      title: 'Followers Growth',
      description: 'Increase your social media followers with real, active accounts',
      icon: <FaUsers className="text-3xl" />,
      features: ['Real Followers', 'Fast Delivery', '24/7 Support', '30-Day Guarantee'],
      platforms: ['Facebook', 'Instagram', 'TikTok', 'Twitter', 'YouTube']
    },
    {
      title: 'Likes & Reactions',
      description: 'Boost your posts with genuine likes and reactions',
      icon: <FaHeart className="text-3xl" />,
      features: ['High Quality Likes', 'Instant Delivery', 'Safe & Secure', 'Money Back Guarantee'],
      platforms: ['Facebook', 'Instagram', 'TikTok', 'Twitter']
    },
    {
      title: 'Views & Impressions',
      description: 'Get more views on your videos and posts',
      icon: <FaEye className="text-3xl" />,
      features: ['Real Views', 'Fast Delivery', 'Retention Guarantee', '24/7 Support'],
      platforms: ['Facebook', 'Instagram', 'TikTok', 'YouTube']
    },
    {
      title: 'Shares & Reposts',
      description: 'Increase your content reach with shares and reposts',
      icon: <FaShare className="text-3xl" />,
      features: ['Organic Shares', 'Quick Delivery', 'Targeted Audience', 'Safe Methods'],
      platforms: ['Facebook', 'Instagram', 'TikTok', 'Twitter']
    },
    {
      title: 'Comments & Engagement',
      description: 'Boost engagement with authentic comments',
      icon: <FaComment className="text-3xl" />,
      features: ['Custom Comments', 'Fast Delivery', 'Real Accounts', 'Privacy Protected'],
      platforms: ['Facebook', 'Instagram', 'TikTok', 'YouTube']
    },
    {
      title: 'Video Plays',
      description: 'Increase video plays and watch time',
      icon: <FaPlay className="text-3xl" />,
      features: ['High Retention', 'Fast Start', 'Real Plays', 'Guaranteed Results'],
      platforms: ['TikTok', 'YouTube', 'Instagram']
    }
  ];

  const platformIcons = {
    'Facebook': <FaFacebook className="text-blue-600" />,
    'Instagram': <FaInstagram className="text-pink-600" />,
    'TikTok': <FaTiktok className="text-black" />,
    'Twitter': <FaTwitter className="text-sky-500" />,
    'YouTube': <FaYoutube className="text-red-600" />
  };

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="hero-gradient text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Our Premium Services
            </h1>
            <p className="text-xl text-purple-100 max-w-3xl mx-auto">
              Comprehensive social media growth solutions for all platforms. Boost your presence with real, engaging followers and interactions.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-premium-white-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="card p-8 hover:scale-105 transition-transform duration-300">
                <div className="w-16 h-16 bg-purple-900 bg-opacity-50 rounded-full flex items-center justify-center text-purple-400 mb-6">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{service.title}</h3>
                <p className="text-white mb-6">{service.description}</p>
                
                <div className="mb-6">
                  <h4 className="font-semibold text-white mb-3">Features:</h4>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-white">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mb-6">
                  <h4 className="font-semibold text-white mb-3">Available Platforms:</h4>
                  <div className="flex flex-wrap gap-3">
                    {service.platforms.map((platform, idx) => (
                      <div key={idx} className="flex items-center space-x-2 bg-premium-purple-200 px-3 py-1 rounded-full">
                        {platformIcons[platform]}
                        <span className="text-sm text-white">{platform}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Link to="/packages" className="btn-primary w-full text-center">
                  Get Started
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gradient-to-br from-premium-purple-200 to-premium-purple-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-white text-lg">
              Simple 3-step process to grow your social media
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Choose Service</h3>
              <p className="text-white">Select service and package that fits your needs</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Provide Details</h3>
              <p className="text-white">Enter your username and select your preferences</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">See Results</h3>
              <p className="text-white">Watch your social media presence grow rapidly</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 hero-gradient text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-purple-100">
            Choose from our wide range of services and start growing your social media today
          </p>
          <Link to="/packages" className="btn-primary bg-white text-white hover:bg-purple-50 text-lg px-8 py-4">
            View All Packages
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
